import { Link, useParams } from 'react-router-dom';
import Seo, { baseOrgSchema } from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList, mangaCategories } from '@/mocks/manga';
import { siteConfig } from '@/config/site';

function Article() {
  const { slug } = useParams();
  const item = mangaList.find((m) => m.slug === slug);

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-32 text-center md:px-6">
        <i className="ri-error-warning-line text-5xl text-foreground-400"></i>
        <h1 className="mt-4 text-xl font-bold text-foreground-950">未找到该漫画</h1>
        <Link to="/category" className="mt-4 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50">
          返回全部漫画
        </Link>
      </div>
    );
  }

  const category = mangaCategories.find((c) => c.name === item.category);
  const related = mangaList
    .filter((m) => m.slug !== item.slug && m.category === item.category)
    .concat(mangaList.filter((m) => m.slug !== item.slug && m.category !== item.category).sort((a, b) => b.views - a.views))
    .slice(0, 8);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    ...(category ? [{ name: '题材分类', path: '/category' }] : []),
    ...(category ? [{ name: category.name, path: `/category/${category.slug}` }] : []),
    { name: item.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title,
    description: item.intro,
    image: [item.cover],
    author: { '@type': 'Person', name: item.authors },
    datePublished: '2026-01-15',
    dateModified: item.update,
    mainEntityOfPage: `${siteConfig.url}/article/${item.slug}`,
    publisher: { '@type': 'Organization', name: siteConfig.name },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${siteConfig.url}/` },
      ...(category
        ? [
            { '@type': 'ListItem', position: 2, name: '题材分类', item: `${siteConfig.url}/category` },
            { '@type': 'ListItem', position: 3, name: category.name, item: `${siteConfig.url}/category/${category.slug}` },
          ]
        : []),
      { '@type': 'ListItem', position: category ? 4 : 2, name: item.title, item: `${siteConfig.url}/article/${item.slug}` },
    ],
  };

  return (
    <>
      <Seo
        title={item.title}
        path={`/article/${item.slug}`}
        description={item.intro}
        keywords={`${item.title},${item.title}漫画,${item.category}漫画,${item.tags.join(',')}漫画`}
        jsonLd={[baseOrgSchema, articleSchema, breadcrumbSchema]}
      />

      <div className="mx-auto w-full max-w-7xl px-4 pt-28 md:px-6">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* 漫画详情头部 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6" aria-label={`${item.title} 详情`}>
        <div className="flex flex-col gap-8 rounded-lg bg-background-100 p-6 md:flex-row md:p-8">
          <div className="mx-auto w-full max-w-[220px] shrink-0 md:mx-0">
            <div className="overflow-hidden rounded-lg">
              <img
                src={item.cover}
                alt={`${item.title} 漫画封面`}
                title={`${item.title} 漫画在线看`}
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-bold text-background-50 ${item.status === 'finished' ? 'bg-secondary-500' : 'bg-primary-500'}`}>
                {item.status === 'finished' ? '已完结' : '连载中'}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-800">{item.category}</span>
            </div>

            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{item.title}</h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span><i className="ri-user-line mr-1 text-primary-500"></i>{item.authors}</span>
              <span><i className="ri-star-fill mr-1 text-accent-500"></i>{item.rating} 分</span>
              <span><i className="ri-eye-line mr-1 text-primary-500"></i>{(item.views / 10000).toFixed(1)}万 阅读</span>
              <span><i className="ri-book-open-line mr-1 text-secondary-600"></i>{item.chapters} 话</span>
              <span><i className="ri-time-line mr-1 text-primary-500"></i>更新于 {item.update}</span>
            </div>

            <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">{item.intro}</p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {item.tags.map((t) => (
                <Link key={t} to={`/category?tag=${encodeURIComponent(t)}`} className="rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-800">
                  <i className="ri-price-tag-3-line mr-0.5 text-accent-500"></i>{t}
                </Link>
              ))}
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-circle-line mr-2 text-base"></i>立即开始阅读
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-8 py-3 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-heart-3-line mr-2 text-base"></i>加入收藏
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 相关推荐 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6" aria-labelledby="related-title">
        <div className="mb-6 flex items-center gap-2">
          <span className="flex h-8 w-1 rounded-full bg-primary-500"></span>
          <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
          {related.map((r) => (
            <MangaCard key={r.slug} item={r} />
          ))}
        </div>
      </section>
    </>
  );
}

export default Article;